import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {specialOptionsForRelateToRiskDropdown} from '../../../../../../../../util/uiconstants';
import {getRisksSlice} from '../../../../../../../../selectors/ISA315/selectors';
import SelectDropdown from '../../../../../../SelectDropdown/SelectDropdown';
import EllipsesControl from '../../../../../../EllipsesControl/EllipsesControl';
import {useOverrideLabels} from '../../../../../../../Main/Forms/Utilities/OverrideLabelsProvider';
import InlineMessage from '../../../../../../BaseComponentWrappers/MotifInlineMessage';
import Button from '../../../../../../BaseComponentWrappers/MotifButton';
import Icon from '../../../../../../BaseComponentWrappers/MotifIcon';
import {contentIcClear24px} from '@ey/motif-react-wrapper/assets/icons/index';
import Tooltip from '../../../../../../BaseComponentWrappers/MotifTooltip';

export default function RelatedRisksDescriptionDropdown({
	selectedRiskId,
	isDisabled,
	onRiskChange,
	riskTypeId,
	onNewRiskClick,
	isCleared,
	showClearIcon
}) {
	const risksById = useSelector((state) => getRisksSlice(state)?.byId);
	const _riskType = risksById && selectedRiskId > 0 && !riskTypeId ? risksById[selectedRiskId]?.riskTypeId : riskTypeId;
	const {labels, currentResource} = useOverrideLabels();
	const [onRelateDropdownFocus, setOnRelateDropdownFocus] = useState(false);

	const buildRiskOption = useCallback(
		(risk) => {
			let riskOption = {};
			if (risk) {
				if (_riskType && _riskType !== risk.riskTypeId) {
					return {};
				}
				riskOption = {
					value: risk.id,
					label: risk.riskName,
					isNotMaterial: false,
					data: risk
				};
				return riskOption;
			}
			return riskOption;
		},
		[selectedRiskId, riskTypeId, risksById, _riskType]
	);

	const noRisksIdentifiedOption = {
		value: specialOptionsForRelateToRiskDropdown.noRisksIdentified,
		label: labels.noRisksIdentified,
		isNotMaterial: false,
		isDisabled: true
	};

	const selectedRisk = risksById && buildRiskOption(risksById[selectedRiskId]);

	const handleOnRiskChange = (option) => {
		onRiskChange && onRiskChange(option);
		setOnRelateDropdownFocus(false);
	};

	const handleOnNewRiskClick = useCallback(() => {
		onNewRiskClick && onNewRiskClick();
	}, []);

	const renderFooter = useCallback(() => {
		return (
			<div className="newrisk-btn">
				<Button variant="text-alt" onClick={handleOnNewRiskClick} showLabel buttonLabel={labels.newRisk} />
			</div>
		);
	}, [handleOnNewRiskClick]);

	const getDropdownOptions = useCallback(() => {
		let riskOptions = [];
		let savedRiskOptions = [];
		if (!isDisabled) {
			if (!risksById) {
				riskOptions.push(noRisksIdentifiedOption);
				return riskOptions;
			}

			let filteredRisks = Object.values(risksById).filter(
				(f) => f.riskTypeId === Number(currentResource.riskTypes[_riskType - 1]?.id)
			);
			for (const risk of filteredRisks) {
				riskOptions.push(buildRiskOption(risk));
			}

			let risks = savedRiskOptions.find(
				(f) => Number(f.riskTypeId) === Number(currentResource.riskTypes[_riskType - 1]?.id)
			)?.options;
			if (risks?.length > 0) {
				for (const risk of risks) {
					//Add saved risks to options
					riskOptions.push({
						label: risk.riskName,
						value: risk.id
					});
				}
			}
			if (!(risks?.length > 0 || filteredRisks?.length > 0)) {
				//Add "No risks identified" option
				riskOptions.push(noRisksIdentifiedOption);
			}
			return riskOptions;
		}
	}, [buildRiskOption]);

	const formatOptionLabel = useCallback((option) => {
		return (
			<section
				//If it's NOT A ROMM, it should be bold
				className={`assessRiskOptionText ${option.label === labels.noRisksIdentified ? 'disabled-option' : ''}`}
			>
				<EllipsesControl
					content={option.label}
					tooltip={option.label}
					noOfLines={1}
					tooltipClass="tooltipCustomWidth"
				/>
			</section>
		);
	}, []);
	const warningMessage = (!isDisabled && !selectedRisk) || (selectedRisk && !(Object.entries(selectedRisk).length > 0));

	const customClearIcon = (
		<Tooltip
			className={'ClearRiskSignificanceTooltip'}
			// title={labels.clearRiskSignificance}
			variant="alt"
			trigger={<Icon src={contentIcClear24px} />}
			placement="top"
			closeButtonProps={{
				className: 'hide'
			}}
			allowClick
			allowHover
			children={
				<>
					<span>{labels.clearRiskSignificance}</span>
				</>
			}
		/>
	);

	return (
		<>
			<StyledRelatedRisksDescriptionDropdown
				className="related-desc-dropdown"
				isDisabled={isCleared || isDisabled}
				formLabel={labels.relateToRisk}
				isSearchable
				id="risks"
				noOptionsMessage={() => labels.noResultsFound}
				openMenuOnFocus={onRelateDropdownFocus}
				onMenuOpen={() => {
					setOnRelateDropdownFocus(true);
				}}
				formatOptionLabel={formatOptionLabel}
				defaultValue={selectedRisk}
				value={selectedRisk}
				isOptionDisabled={(option) => option.isDisabled}
				options={getDropdownOptions()}
				onChange={handleOnRiskChange}
				footer={renderFooter()}
				isClearable={showClearIcon && !warningMessage} //Hide clear icon if no related risk is defined.
				customClearIcon
				customClearIconComponent={customClearIcon}
			/>
			{warningMessage && !isCleared && (
				<InlineMessage
					className={'inline-message'}
					variant="warning"
					children={<span className="error-message">{labels.notDefined}</span>}
				/>
			)}
		</>
	);
}

RelatedRisksDescriptionDropdown.propTypes = {
	selectedRiskId: PropTypes.number,
	isDisabled: PropTypes.bool,
	onRiskChange: PropTypes.func,
	riskTypeId: PropTypes.number,
	onNewRiskClick: PropTypes.func
};

const StyledRelatedRisksDescriptionDropdown = styled(SelectDropdown)`
	.react-select-container
		.react-select__menu
		.react-select__menu-list
		.react-select__group
		.react-select__group-heading {
		padding-top: var(--px-12);
		padding-bottom: var(--px-12);
	}
	.newrisk-btn {
		padding: var(--px-12);
		border-top: var(--px-1) solid var(--neutrals-700);
		background-color: var(--neutrals-00white);
	}
	.react-select__menu .nota-romm {
		font-size: var(--px-14);
		font-weight: bold;
		padding-top: var(--px-4);
		padding-bottom: var(--px-4);
	}
	.react-select__menu {
		.react-select__option {
			cursor: default;
			padding: 0;
			.assessRiskOptionText {
				cursor: pointer;
				padding: 0;
				line-height: var(--px-20);
				&.disabled-option {
					cursor: default;
				}
			}
		}
	}
`;
